import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SAINUsageEventPayload } from '../../controller/analytics/hooks/use-sain-usage';
import { AIFeedbackV2 } from '../ai-feedback-v2';

const styles = cssMap({
	container: {
		height: '30px', // Set specific height to avoid layout shift when feedback buttons are shown
		paddingBlockEnd: token('space.050', '4px'),
	},
});

const messages = defineMessages({
	heading: {
		id: 'search_ai.ai_answer_top_bookend.heading',
		defaultMessage: 'Overview',
		description: 'Heading text for smart answer content',
	},
});

type AIAnswerTopBookendPropsWithFeedbackOptions = {
	showFeedbackButtons?: true;
	feedbackOptions: {
		attributes: object;
		attributesWithConsent: object;
		entrypointId: string;
		sainUsageEventPayload?: Omit<SAINUsageEventPayload, 'metCriteria'>;
		onLikeButtonClickHandler?: () => void;
		onDislikeButtonClickHandler?: () => void;
		userDetails: {
			id?: string;
			name?: string;
		};
		customContent?: React.ReactNode;
	};
};

type AIAnswerTopBookendPropsWithoutFeedbackOptions = {
	showFeedbackButtons: false;
	feedbackOptions?: never;
};

type AIAnswerTopBookendProps =
	| AIAnswerTopBookendPropsWithFeedbackOptions
	| AIAnswerTopBookendPropsWithoutFeedbackOptions;

export function AIAnswerTopBookend({
	showFeedbackButtons,
	feedbackOptions,
}: AIAnswerTopBookendProps) {
	return (
		<Inline alignBlock="center" spread="space-between" xcss={styles.container}>
			<Heading as="h2" size="xsmall">
				<FormattedMessage {...messages.heading} />
			</Heading>
			{showFeedbackButtons ? (
				<AIFeedbackV2
					entrypointId={feedbackOptions.entrypointId}
					userDetails={feedbackOptions.userDetails}
					attributes={feedbackOptions.attributes}
					attributesWithConsent={feedbackOptions.attributesWithConsent}
					onLikeButtonClickHandler={feedbackOptions.onLikeButtonClickHandler}
					onDislikeButtonClickHandler={feedbackOptions.onDislikeButtonClickHandler}
					customContent={feedbackOptions.customContent}
					isCompact={true}
				/>
			) : null}
		</Inline>
	);
}
